'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { useFullscreen } from '@/contexts/games/hooks/useFullscreen';
import { UniversalGameHeader } from './UniversalGameHeader';
import { useGameTimer } from '@/contexts/games/hooks/useGameTimer';
import { cn } from '@/shared/utils/cn';
import { Button } from "@/shared/components/ui/button";

type UseGameTimerReturn = ReturnType<typeof useGameTimer>;

interface GameLayoutProps {
  gameId: string;
  title: string;
  children: React.ReactNode;
  settings?: React.ReactNode;
  showSettings?: boolean;
  timer?: UseGameTimerReturn;
  stats?: {
    target?: number;
    score?: number;
    moves?: number;
  };
  showBackButton?: boolean;
  backUrl?: string;
  className?: string;
  gameAreaClassName?: string;
  onSettingsToggle?: (isOpen: boolean) => void;
  onNewGame?: () => void;
  onShowRules?: () => void;
  extraActions?: React.ReactNode;
}

/**
 * Универсальный layout для всех игр
 * Предоставляет:
 * - Адаптивную игровую область
 * - Боковую панель настроек
 * - Полноэкранный режим
 * - Навигацию
 * - Единый стиль
 */
export function GameLayout({
  title,
  children,
  settings,
  showSettings = true,
  timer,
  stats,
  className,
  onSettingsToggle,
  onNewGame,
  onShowRules,
  extraActions,
}: GameLayoutProps) {
  const t = useTranslations();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(
    gameContainerRef.current
  );

  // Обработка открытия/закрытия настроек (используется в UniversalGameHeader)
  const handleSettingsToggle = useCallback(() => {
    const newState = !isSettingsOpen;
    setIsSettingsOpen(newState);
    onSettingsToggle?.(newState);
  }, [isSettingsOpen, onSettingsToggle]);

  // Закрытие настроек при клике вне панели
  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      if (!isSettingsOpen) return;
      const target = event.target as Element | null;
      if (!target) return;
      // Inside the panel → keep. On the gear itself → its own click toggles
      // (closing here as well would re-open it). Anywhere else → close.
      // (The old check required a `#game-settings-button` element that never
      // existed, so this handler could not fire at all.)
      if (target.closest('[data-testid="game-layout-settings-panel"]')) return;
      if (target.closest('[data-testid="game-settings-button"]')) return;
      setIsSettingsOpen(false);
      onSettingsToggle?.(false);
    };

    // pointerdown, not click: a tap on the canvas starts a draw (pointer capture)
    // and never produces a click, so the panel used to stay open until the ✕.
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [isSettingsOpen, onSettingsToggle]);

  // Обработка клавиши Escape для закрытия настроек
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSettingsOpen) {
        setIsSettingsOpen(false);
        onSettingsToggle?.(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSettingsOpen, onSettingsToggle]);

  // Закрытие настроек при клике вне области
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsSettingsOpen(false);
      onSettingsToggle?.(false);
    }
  }, [onSettingsToggle]);

  return (
    <div
      ref={gameContainerRef}
      data-testid="game-layout"
      className={cn(
        // Fills the shell's <main> (the (play) layout makes it exactly the viewport
        // minus the mobile top bar, non-scrolling); fullscreen overlays everything.
        'relative flex h-full min-h-0 w-full flex-col overflow-hidden',
        isFullscreen && 'fixed inset-0 z-50 h-dvh bg-linear-to-br from-blue-900 via-purple-900 to-pink-900',
        className
      )}
    >
      {/* Заголовок с интегрированным управлением */}
      <UniversalGameHeader
        title={title}
        isFullscreen={isFullscreen}
        onFullscreenToggle={() => {
          toggleFullscreen();
          // Принудительно обновляем размеры через небольшую задержку
          setTimeout(() => {
            const event = new Event('resize');
            window.dispatchEvent(event);
          }, 100);
        }}
        onSettingsToggle={handleSettingsToggle}
        onNewGame={onNewGame || (() => {})}
        onShowRules={onShowRules || (() => {})}
        timer={timer || {
          formattedTime: '00:00', 
          isRunning: false,
          elapsedTime: 0,
          isPaused: false,
          start: () => {},
          pause: () => {},
          resume: () => {},
          reset: () => {},
          stop: () => {}
        }}
        stats={{
          target: stats?.target || 0,
          score: stats?.score || 0,
          moves: stats?.moves || 0
        }}
        extraActions={extraActions}
      />

      {/* Панель настроек (плавающая) */}
      {showSettings && settings && isSettingsOpen && (
        <>
          {/* Backdrop для закрытия при клике вне области */}
          <div
            className="absolute inset-0 z-10 bg-black/20 backdrop-blur-xs animate-in fade-in duration-200"
            onClick={handleBackdropClick}
            data-testid="game-settings-backdrop"
          />
          {/* Панель настроек */}
          <div className="absolute top-10 sm:top-16 left-2 right-2 sm:left-4 sm:right-4 z-20 bg-card/95 dark:bg-card/98 backdrop-blur-md rounded-xl shadow-xl border border-border p-3 sm:p-4 animate-in slide-in-from-top-2 duration-200" data-testid="game-layout-settings-panel">
            {/* Заголовок с кнопкой закрытия */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-foreground">{t('games.settings')}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setIsSettingsOpen(false);
                  onSettingsToggle?.(false);
                }}
                className="h-8 w-8"
                aria-label={t('games.closeSettings')}
                data-testid="game-settings-close-button"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
            
            {settings}
          </div>
        </>
      )}

      {/* Основная игровая область */}
      <div
        data-testid="game-content-area"
        className={cn(
          // The play area = whatever height is left under the header; children
          // size themselves to it (min-h-0 lets a flex child actually shrink).
          "flex min-h-0 flex-1 items-center justify-center overflow-hidden",
          isFullscreen ? "px-2 py-1 sm:px-6 sm:py-3" : "px-1 py-1 sm:px-4 sm:py-2",
        )}
      >
        {children}
      </div>
    </div>
  );
}
